import { ElementType, useContext, useEffect, useRef } from "react"
import {
    Box,
    Paper,
    Stack,
    Typography,
    IconButton,
    Tooltip,
    Card,
    CardContent,
    Chip,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material"
import {
    Circle,
    PlayArrow,
    Memory,
    Speed,
    ExpandMore,
    CheckCircle,
    Error as ErrorIcon,
    Warning,
    Help,
    KeyboardArrowUp,
    KeyboardArrowDown
} from "@mui/icons-material"
import { AdminContext } from "@my-helpers/common";

const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
        case 'RUNNING':
            return 'success';
        case 'ACTIVE':
            return 'success';
        case 'STOPPED':
        case 'STOPPING':
            return 'error';
        case 'PENDING':
            return 'warning';
        default:
            return 'default';
    }
};

const getHealthIcon = (healthStatus: string, fontSize: "small" | "medium" | "large" = "medium") => {
    switch (healthStatus?.toUpperCase()) {
        case 'HEALTHY':
            return <CheckCircle color="success" fontSize={fontSize} />;
        case 'UNHEALTHY':
            return <ErrorIcon color="error" fontSize={fontSize} />;
        case 'UNKNOWN':
            return <Help color="disabled" fontSize={fontSize} />;
        default:
            return <Warning color="warning" fontSize={fontSize} />;
    }
};

const ServiceStatus: ElementType = () => {
    const ctx = useContext(AdminContext);
    if (!ctx) {
        throw new Error('ServiceStatus must be used within an AdminProvider');
    }
    const { services } = ctx.view;
    const { 
        updateService,
        refreshServiceStatus
    } = ctx.command;
    const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Auto-refresh when desired and running counts don't match
    useEffect(() => {
        if (!services || services.length === 0) return;

        const hasMismatch = services.some(service => 
            service.runningCount !== service.desiredCount
        );

        if (hasMismatch) {
            // Clear any existing timeout
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }

            // Set a debounced refresh after 6 seconds
            refreshTimeoutRef.current = setTimeout(() => {
                refreshServiceStatus();
            }, 6000);
        }

        // Cleanup timeout on unmount
        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
        };
    }, [services, refreshServiceStatus]);

    return (
        <Paper elevation={3} sx={{ p: 3, maxWidth: 1200, margin: 'auto' }}>
                <Typography variant="h6" marginBottom={1}>
                    Service Status
                </Typography>

                <Grid container spacing={3}>
                    {services && services.map((service, index) => (
                        <Grid size={{ xs: 12, md: 6 }} key={service.serviceName || index}>
                            <Card
                                elevation={2}
                                sx={{
                                    height: '100%',
                                    border: service.runningCount === service.desiredCount && service.desiredCount > 0 ? '2px solid' : '1px solid',
                                    borderColor: service.runningCount === service.desiredCount && service.desiredCount > 0 ? 'success.main' : service.desiredCount === 0 ? 'divider' : 'error.main'
                                }}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                        <Typography variant="h6" component="h3">
                                            {service.serviceName}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip
                                                label={service.status}
                                                color={getStatusColor(service.status || '')}
                                                size="small"
                                            />
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => updateService(service.serviceName, service.desiredCount + 1)}
                                                    sx={{ p: 0.25, minWidth: 0, height: 16 }}
                                                >
                                                    <KeyboardArrowUp fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => updateService(service.serviceName, service.desiredCount - 1)}
                                                    disabled={service.desiredCount <= 0}
                                                    sx={{ p: 0.25, minWidth: 0, height: 16 }}
                                                >
                                                    <KeyboardArrowDown fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {service.tasks && service.tasks.length > 0 && (
                                        <Accordion
                                            sx={{
                                                '& .MuiAccordionSummary-root': {
                                                    minHeight: 'auto',
                                                    py: 0.5,
                                                    '&.Mui-expanded': {
                                                        minHeight: 'auto',
                                                        mb: -1
                                                    }
                                                },
                                                '& .MuiAccordionDetails-root': {
                                                    pt: 0,
                                                    pb: 1,
                                                    mt: -1
                                                }
                                            }}
                                        >
                                            <AccordionSummary expandIcon={<ExpandMore />}>
                                                <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Circle color="primary" sx={{ fontSize: '0.85rem' }}/>
                                                        <Typography variant="body2" sx={{ fontSize: '0.80rem' }}>
                                                            Desired: {service.desiredCount}
                                                        </Typography>
                                                    </Box>
                                                    {(service.pendingCount || 0) > 0 && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Warning color="warning" sx={{ fontSize: '0.85rem' }} />
                                                            <Typography variant="body2" sx={{ fontSize: '0.80rem' }}>
                                                                Pending: {service.pendingCount}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <PlayArrow color="success" sx={{ fontSize: '1.2rem' }} />
                                                        <Typography variant="body2" sx={{ fontSize: '0.80rem' }}>
                                                            Running: {service.runningCount}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack spacing={2}>
                                                    {service.tasks.map((task, taskIndex) => (
                                                        <Box
                                                            key={taskIndex}
                                                            sx={{
                                                                p: 1,
                                                                bgcolor: 'grey.50',
                                                                borderRadius: 1
                                                            }}>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    mb: 0
                                                                }}>
                                                                <Typography variant="body2" fontWeight="medium">
                                                                    Task v{task.version}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                                    <Chip
                                                                        label={task.lastStatus}
                                                                        color={getStatusColor(task.lastStatus || '')}
                                                                        size="small"
                                                                    />
                                                                    {getHealthIcon(task.healthStatus || '')}
                                                                </Box>
                                                            </Box>

                                                            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                                                                <Tooltip title="CPU allocation for this task">
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <Speed fontSize="small" sx={{ color: 'text.secondary' }} />
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {task.cpu}
                                                                        </Typography>
                                                                    </Box>
                                                                </Tooltip>
                                                                <Tooltip title="Memory allocation for this task">
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <Memory fontSize="small" sx={{ color: 'text.secondary' }} />
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {task.memory}MB
                                                                        </Typography>
                                                                    </Box>
                                                                </Tooltip>
                                                            </Box>

                                                            {task.containers && task.containers.length > 0 && (
                                                                <Box>
                                                                    <Stack spacing={1}>
                                                                        {task.containers.map((container, containerIndex) => (
                                                                            <Box
                                                                                key={containerIndex}
                                                                                sx={{
                                                                                    display: 'flex',
                                                                                    flexDirection: 'column',
                                                                                    justifyContent: 'space-between',
                                                                                    p: 1,
                                                                                    bgcolor: 'white',
                                                                                    borderRadius: 0.5,
                                                                                    border: '1px solid',
                                                                                    borderColor: 'divider'
                                                                                }}
                                                                            >
                                                                                <Box
                                                                                    sx={{
                                                                                        display: 'flex',
                                                                                        justifyContent: 'space-between',
                                                                                        alignItems: 'center',
                                                                                        mb: 0
                                                                                    }}>
                                                                                    <Typography variant="caption" fontWeight="medium" flex={1}>
                                                                                        {container.name}
                                                                                    </Typography>
                                                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                                                        <Chip
                                                                                            label={container.lastStatus}
                                                                                            color={getStatusColor(container.lastStatus || '')}
                                                                                            size="small"
                                                                                            sx={{ height: 20, fontSize: '0.65rem' }}
                                                                                        />
                                                                                        {getHealthIcon(container.healthStatus || '', 'small')}
                                                                                    </Box>
                                                                                </Box>
                                                                                <Box sx={{ display: 'flex', gap: 2 }}>
                                                                                    <Tooltip title="CPU allocation for this container">
                                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                            <Speed sx={{ fontSize: '0.90rem', color: 'text.secondary' }} />
                                                                                            <Typography variant="caption" sx={{ fontSize: '0.65rem' }} color="text.secondary">
                                                                                                {container.cpu}
                                                                                            </Typography>
                                                                                        </Box>
                                                                                    </Tooltip>
                                                                                    <Tooltip title="Memory allocation for this container">
                                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                            <Memory sx={{ fontSize: '0.90rem', color: 'text.secondary' }} />
                                                                                            <Typography variant="caption" sx={{ fontSize: '0.65rem' }} color="text.secondary">
                                                                                                {container.memory}MB
                                                                                            </Typography>
                                                                                        </Box>
                                                                                    </Tooltip>
                                                                                </Box>

                                                                            </Box>
                                                                        ))}
                                                                    </Stack>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>
                                    )}

                                    {(!service.tasks || service.tasks.length === 0) && service.desiredCount === 0 && (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            p: 2,
                                            bgcolor: 'grey.100',
                                            borderRadius: 1
                                        }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Service is scaled down (0 desired instances)
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {services?.length === 0 && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 4,
                        bgcolor: 'grey.50',
                        borderRadius: 1
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            No services found
                        </Typography>
                    </Box>
                )}
        </Paper>
    )
}

export default ServiceStatus
